import { Component, inject, Input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@shared/material/material.module';
import { LanguageService } from '@core/services/language.service';
import { FormsModule } from '@angular/forms';
import { UpperCasePipe } from '@angular/common';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  region: string;
}

interface LanguageGroup {
  region: string;
  languages: Language[];
}

@Component({
  selector: 'app-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule, UpperCasePipe, FormsModule]
})
export class LanguageSelectorComponent {
  private languageService = inject(LanguageService);
  @Input() buttonClass: string | { [key: string]: boolean } = 'default-language-button';

  searchTerm = signal('');

  readonly languages: Language[] = [
    // Middle East & Africa
    { code: 'he', name: 'Hebrew', nativeName: 'עברית', region: 'Middle East & Africa' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', region: 'Middle East & Africa' },
    { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', region: 'Middle East & Africa' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', region: 'Middle East & Africa' },
    // Europe
    { code: 'en', name: 'English', nativeName: 'English', region: 'Europe' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', region: 'Europe' },
    { code: 'fr', name: 'French', nativeName: 'Français', region: 'Europe' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', region: 'Europe' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', region: 'Europe' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português', region: 'Europe' },
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', region: 'Europe' },
    { code: 'pl', name: 'Polish', nativeName: 'Polski', region: 'Europe' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română', region: 'Europe' },
    { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', region: 'Europe' },
    { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', region: 'Europe' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', region: 'Europe' },
    // Asia & Pacific
    { code: 'zh', name: 'Chinese', nativeName: '中文', region: 'Asia & Pacific' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', region: 'Asia & Pacific' },
    { code: 'ko', name: 'Korean', nativeName: '한국어', region: 'Asia & Pacific' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', region: 'Asia & Pacific' },
    { code: 'th', name: 'Thai', nativeName: 'ไทย', region: 'Asia & Pacific' },
    { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', region: 'Asia & Pacific' },
    { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', region: 'Asia & Pacific' },
    { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', region: 'Asia & Pacific' },
  ];

  readonly regionOrder = ['Middle East & Africa', 'Europe', 'Asia & Pacific'];

  filteredGroups = computed<LanguageGroup[]>(() => {
    const term = this.searchTerm().toLowerCase();
    const filtered = term
      ? this.languages.filter(l =>
          l.name.toLowerCase().includes(term) ||
          l.nativeName.toLowerCase().includes(term) ||
          l.code.toLowerCase().includes(term))
      : this.languages;

    return this.regionOrder
      .map(region => ({
        region,
        languages: filtered.filter(l => l.region === region)
      }))
      .filter(g => g.languages.length > 0);
  });

  getCurrentLanguageDisplay(): string {
    const currentLang = this.languageService.currentLang();
    const lang = this.languages.find(l => l.code === currentLang);
    return lang?.nativeName || 'English';
  }

  isCurrentLang(code: string): boolean {
    return this.languageService.currentLang() === code;
  }

  setLanguage(lang: string): void {
    this.languageService.setLanguage(lang);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onMenuOpened(): void {
    this.searchTerm.set('');
  }

  // Prevent menu from closing when clicking the search input
  onSearchClick(event: Event): void {
    event.stopPropagation();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }
}
